import {
  ErrorStatus,
  SheetSizeUnavailableLogo
} from '../../../core/models/entities/ErrorStatus'
import { ErrorResponse } from '../responses/ErrorResponse'
import { enviroment } from '../../../enviroment'

export function mapToErrorStatus(errorDto: ErrorResponse): ErrorStatus {
  return {
    code: errorDto.code,
    message: errorDto.message,
    errorCode: errorDto.errorCode
  }
}

// Marker for a flag-gated route that answered 404 { disabled: true, flag }
// (the admin Stripe payout routes while StripePayoutReconciliationEnabled is
// off). Screens use it to tell "feature off" from "not found"/"server error".
export const DISABLED_ERROR_CODE = 'DISABLED'

// Marker of the API's ProductPendingGuard 409 ("Prices on their way", flag
// productPendingEnabled): { code: "productPending", error: "productPending",
// message, logoIds, retryAfterSeconds }.
export const PRODUCT_PENDING_ERROR_CODE = 'productPending'

// Marker of the API's fail-fast 503 while the logo supplier (app117) is
// down (flag supplierUnavailableEnabled): { code: "supplierUnavailable",
// error: "supplierUnavailable", message, retryAfterSeconds } on upload,
// clone, colour change, method change and resize.
export const SUPPLIER_UNAVAILABLE_ERROR_CODE = 'supplierUnavailable'

// Marker of the API's fail-fast 503 while NAV (the item system) is down or
// restarting (flag navUnavailableEnabled, API flag NavFailFastEnabled):
// { statusCode: 503, code: "navUnavailable", error: "navUnavailable",
// message, reason: "latchOpen" | "timeout" | "transport" | "fault",
// retrySafe, retryAfterSeconds, operation } on place/commit order, the
// delivery-date preview, logo options and the item lookups.
export const NAV_UNAVAILABLE_ERROR_CODE = 'navUnavailable'

// Marker of the API's oversize-sheet guard 409 at CommitOrder/PlaceOrder
// (flag oversizeSheetGuardEnabled): { statusCode: 409, error:
// "sheetSizeUnavailable", message, maxMm, canRequest, logos: [{ logoId,
// serial, title, widthMm, heightMm }], logoIds }.
export const SHEET_SIZE_UNAVAILABLE_ERROR_CODE = 'sheetSizeUnavailable'

// Marker of the upload's sync-path rejection (flag uploadPendingEnabled;
// the API sends it only under UploadRejectMatchV2Enabled): 422
// { statusCode: 422, error: "uploadRejected", message: <friendly English
// text>, uploadFailedReason: <code>, uploadFailedMessage: <the supplier's
// cleaned text> } (UploadRejectedHttp.ToHttpBody).
export const UPLOAD_REJECTED_ERROR_CODE = 'uploadRejected'

// Marker of the stale-page refusal of PUT ops/transfer-methods/max-sizes
// (flag logoMethodMaxSizeEnabled): 409 { statusCode: 409, error:
// "logoMethodMaxSizeConflict", message, state } - somebody else saved the
// max sizes after this page was loaded; nothing was written and `state` is
// the GET shape as it is stored now.
export const LOGO_METHOD_MAX_SIZE_CONFLICT_ERROR_CODE = 'logoMethodMaxSizeConflict'

// Marker of the stale-page refusal of PUT
// ops/transfer-methods/max-sizes/customers (flag
// logoMethodMaxSizeByOrgEnabled): 409 { statusCode: 409, error:
// "logoMethodMaxSizeCustomersConflict", message, state } - somebody else
// saved the customer-specific max sizes after this page was loaded; nothing
// was written and `state` is the GET shape as it is stored now.
export const LOGO_METHOD_MAX_SIZE_CUSTOMERS_CONFLICT_ERROR_CODE =
  'logoMethodMaxSizeCustomersConflict'

// Markers of the refusals of POST organizations/{orgId}/logos/{logoId}/
// sheet-size-request (same flag): 503 { error: "notConfigured" |
// "sendFailed", sent: false, reason } and 409 { error: "notApplicable",
// reason }. Kept as errorCode so the dialog can tell them apart even when
// the API adds a human message.
export const SHEET_SIZE_REQUEST_ERROR_CODES: ReadonlySet<string> = new Set([
  'notConfigured',
  'sendFailed',
  'notApplicable'
])

// Markers of the refusals POST ops/mail-test/approval-run (Mail test "Real
// run", flag mailTestApprovalRunEnabled) answers as 409 / 422 { error: <one
// of these>, message: <human text>, ... } - never with an errorCode member
// (MailTestApprovalRunPolicy.Error* in the API). No other route uses them.
export const MAIL_TEST_APPROVAL_RUN_ERROR_CODES: ReadonlySet<string> = new Set([
  'allowlistEmpty',
  'sourceFlagOff',
  'noRedisHandle',
  'noPendingItems',
  'mixedCustomers',
  'customerNotFound',
  'notDirectCustomer'
])

// The ErrorStatus for a non-OK response body. Bodies come in three shapes:
//  - the JSON envelope { code, message[, errorCode] } -> mapped as before;
//  - a JSON string (BadRequestObjectResult("...")) -> the HTTP status + text;
//  - any other JSON object ({ error }, { disabled, flag }, ...) or non-JSON
//    text -> the HTTP status, the best message the body offers, and the
//    DISABLED marker when the body says so.
// Envelope bodies keep their exact result; only bodies that used to yield
// code/message undefined now carry the HTTP status.
export function errorStatusFromBody(
  httpStatus: number,
  bodyText: string
): ErrorStatus {
  let parsed: unknown
  try {
    parsed = JSON.parse(bodyText)
  } catch {
    return { code: httpStatus, message: bodyText }
  }
  if (typeof parsed === 'string') {
    return { code: httpStatus, message: parsed }
  }
  if (parsed === null || typeof parsed !== 'object') {
    return { code: httpStatus, message: bodyText }
  }
  const body = parsed as Partial<ErrorResponse> & {
    error?: unknown
    disabled?: unknown
  }
  const status = mapToErrorStatus(body as ErrorResponse)
  if (typeof status.code !== 'number') status.code = httpStatus
  if (typeof status.message !== 'string') {
    status.message = typeof body.error === 'string' ? body.error : bodyText
  }
  if (body.disabled === true && status.errorCode === undefined) {
    status.errorCode = DISABLED_ERROR_CODE
  }
  // Flag productPendingEnabled: the guard's 409 carries its marker in the
  // STRING `code` / `error` and a human `message`. The string `code` was just
  // replaced by the HTTP status and the message never names the marker, so
  // without this the envelope would be indistinguishable from any other 409
  // and the one automatic retry would never fire. Keep the marker as
  // errorCode (nothing else sets one on this body) and carry
  // retryAfterSeconds. Flag off = the status exactly as above.
  // Flag cloneInstantEnabled: the clone-upload guard answers the SAME
  // envelope with reason "cloneUpload" - recognised on its own flag too (K3
  // may be on without R5) and the reason is carried for the toast text.
  if (
    (enviroment.productPendingEnabled || enviroment.cloneInstantEnabled) &&
    httpStatus === 409
  ) {
    const raw = body as {
      code?: unknown
      error?: unknown
      retryAfterSeconds?: unknown
      reason?: unknown
    }
    const marked = [raw.code, raw.error].some(
      (value) =>
        typeof value === 'string' &&
        value.toLowerCase() === PRODUCT_PENDING_ERROR_CODE.toLowerCase()
    )
    if (marked) {
      if (status.errorCode === undefined) status.errorCode = PRODUCT_PENDING_ERROR_CODE
      const seconds = Number(raw.retryAfterSeconds)
      if (Number.isFinite(seconds) && seconds > 0) status.retryAfterSeconds = seconds
      if (enviroment.cloneInstantEnabled && typeof raw.reason === 'string' && raw.reason) {
        status.reason = raw.reason
      }
    }
  }
  // Flag logoPriceRefreshNowEnabled: the admin "Refresh prices now" route
  // answers 429 rateLimited and 409 alreadyPending with retryAfterSeconds
  // (the API's Redis claim / the pending stamp's remaining cooldown). The
  // block above carries that field only for the productPending 409, so the
  // 429 - and a 409 with another marker - would lose it and the button
  // would fall back to a flat 60 s. errorCode already comes from the
  // body's errorCode (the API sends it beside code/error for exactly this
  // reader). Never overwrites a value set above. Flag off = the status
  // exactly as above.
  if (
    enviroment.logoPriceRefreshNowEnabled &&
    (httpStatus === 429 || httpStatus === 409) &&
    status.retryAfterSeconds === undefined
  ) {
    const raw = body as { retryAfterSeconds?: unknown }
    if (
      typeof raw.retryAfterSeconds === 'number' &&
      Number.isFinite(raw.retryAfterSeconds) &&
      raw.retryAfterSeconds > 0
    ) {
      status.retryAfterSeconds = raw.retryAfterSeconds
    }
  }
  // Flag supplierUnavailableEnabled (toast / kept file) or
  // supplierStatusEnabled (the banner's immediate re-check): the API's
  // fail-fast 503 while app117 is down carries its marker in the STRING
  // `code` / `error` (replaced by the HTTP status above, exactly as the
  // productPending 409), the seconds the health latch still holds and the
  // reason ("latchOpen" = refused before any write, "timeout" /
  // "transport" = lost the supplier inside the request). Keep the marker as
  // errorCode (never overwriting an explicit one), carry retryAfterSeconds
  // and reason, so the toast/policy can tell "supplier down" from any other
  // 503. Both flags off = the status exactly as above.
  if (
    (enviroment.supplierUnavailableEnabled || enviroment.supplierStatusEnabled) &&
    httpStatus === 503
  ) {
    const raw = body as {
      code?: unknown
      error?: unknown
      retryAfterSeconds?: unknown
      reason?: unknown
    }
    const marked = [raw.code, raw.error].some(
      (value) =>
        typeof value === 'string' &&
        value.toLowerCase() === SUPPLIER_UNAVAILABLE_ERROR_CODE.toLowerCase()
    )
    if (marked) {
      if (status.errorCode === undefined) {
        status.errorCode = SUPPLIER_UNAVAILABLE_ERROR_CODE
      }
      const seconds = Number(raw.retryAfterSeconds)
      if (
        status.retryAfterSeconds === undefined &&
        Number.isFinite(seconds) &&
        seconds > 0
      ) {
        status.retryAfterSeconds = seconds
      }
      if (
        status.reason === undefined &&
        typeof raw.reason === 'string' &&
        raw.reason !== ''
      ) {
        status.reason = raw.reason
      }
    }
  }
  // Flag navUnavailableEnabled: the API's fail-fast 503 navUnavailable
  // (NavFailFastEnabled) while NAV is down carries its marker in the STRING
  // `code` / `error` (replaced by the HTTP status above, exactly as the
  // supplierUnavailable 503), the seconds the NAV health latch still holds,
  // the reason ("latchOpen" = refused before any NAV call, "timeout" /
  // "transport" / "fault" = lost NAV inside the request) and retrySafe
  // (true only for latchOpen: nothing was written, one automatic retry is
  // allowed). Keep the marker as errorCode (never overwriting an explicit
  // one), carry retryAfterSeconds, reason and retrySafe (a boolean only),
  // so the toast / retry policy can tell "NAV down" from any other 503.
  // Flag off = the status exactly as above.
  if (enviroment.navUnavailableEnabled && httpStatus === 503) {
    const raw = body as {
      code?: unknown
      error?: unknown
      retryAfterSeconds?: unknown
      reason?: unknown
      retrySafe?: unknown
    }
    const marked = [raw.code, raw.error].some(
      (value) =>
        typeof value === 'string' &&
        value.toLowerCase() === NAV_UNAVAILABLE_ERROR_CODE.toLowerCase()
    )
    if (marked) {
      if (status.errorCode === undefined) {
        status.errorCode = NAV_UNAVAILABLE_ERROR_CODE
      }
      const seconds = Number(raw.retryAfterSeconds)
      if (
        status.retryAfterSeconds === undefined &&
        Number.isFinite(seconds) &&
        seconds > 0
      ) {
        status.retryAfterSeconds = seconds
      }
      if (
        status.reason === undefined &&
        typeof raw.reason === 'string' &&
        raw.reason !== ''
      ) {
        status.reason = raw.reason
      }
      if (typeof raw.retrySafe === 'boolean') {
        status.retrySafe = raw.retrySafe
      }
    }
  }
  // Flag adminPriceAdjustmentEnabled: the price-adjustment PUT answers a
  // deterministic Stripe refusal (amount below the per-currency minimum, an
  // idempotency mismatch, ...) as 400 { outcome: "invalid", error:
  // "stripeRejected", detail: <Stripe's message> }. That body has no
  // `message`, so `error` became the message above and the raw text - and
  // with it `detail` - would be gone by the time the DataError reaches the
  // repository. Carry `detail` so the dialog can tell the admin what Stripe
  // rejected instead of a bare code. Flag off = the status exactly as above.
  if (enviroment.adminPriceAdjustmentEnabled && httpStatus === 400) {
    const raw = body as { detail?: unknown }
    if (typeof raw.detail === 'string' && raw.detail.trim() !== '') {
      status.detail = raw.detail
    }
  }
  // Flag mailTestApprovalRunEnabled: POST ops/mail-test/approval-run refuses
  // with 409 { error: "allowlistEmpty" | "sourceFlagOff" | "noRedisHandle",
  // message, ... } and 422 { error: "noPendingItems" | "mixedCustomers" |
  // "customerNotFound" | "notDirectCustomer", message, ... }. The marker
  // sits in the STRING `error` only, `message` is human text that never
  // names it, and there is no errorCode member - and above `error` is used
  // only when the body has no message. So the "Real run" panel could not
  // tell the allowlist dialog (409 allowlistEmpty) or the two amber 422s
  // from any other refusal. Keep the marker as errorCode for exactly this
  // route's codes (never overwriting an explicit one; any other 409/422
  // body is untouched). Flag off = the status exactly as above.
  if (
    enviroment.mailTestApprovalRunEnabled &&
    (httpStatus === 409 || httpStatus === 422) &&
    status.errorCode === undefined &&
    typeof body.error === 'string' &&
    MAIL_TEST_APPROVAL_RUN_ERROR_CODES.has(body.error)
  ) {
    status.errorCode = body.error
  }
  // Flag oversizeSheetGuardEnabled: the oversize-sheet guard's 409 at
  // CommitOrder/PlaceOrder carries its marker in the STRING `error` only
  // (`message` is human text), plus the offending logos, the limit and
  // whether a request can be sent. Keep the marker as errorCode (never
  // overwriting an explicit one) and carry logos/maxMm/canRequest so the
  // dialog lists them with 0 further calls. The request route's own
  // refusals (503 notConfigured / sendFailed, 409 notApplicable) keep
  // their marker the same way. Flag off = the status exactly as above.
  if (enviroment.oversizeSheetGuardEnabled && typeof body.error === 'string') {
    if (
      httpStatus === 409 &&
      body.error.toLowerCase() === SHEET_SIZE_UNAVAILABLE_ERROR_CODE.toLowerCase()
    ) {
      if (status.errorCode === undefined) {
        status.errorCode = SHEET_SIZE_UNAVAILABLE_ERROR_CODE
      }
      const raw = body as { logos?: unknown; maxMm?: unknown; canRequest?: unknown }
      if (Array.isArray(raw.logos)) {
        status.logos = raw.logos
          .filter((l): l is Record<string, unknown> => !!l && typeof l === 'object')
          .map(
            (l): SheetSizeUnavailableLogo => ({
              logoId: Number(l['logoId']),
              serial: typeof l['serial'] === 'string' ? l['serial'] : '',
              title: typeof l['title'] === 'string' ? l['title'] : '',
              widthMm: Number(l['widthMm']),
              heightMm: Number(l['heightMm'])
            })
          )
          .filter((l) => Number.isFinite(l.logoId) && l.logoId > 0)
      }
      const maxMm = Number(raw.maxMm)
      if (Number.isFinite(maxMm) && maxMm > 0) status.maxMm = maxMm
      if (typeof raw.canRequest === 'boolean') status.canRequest = raw.canRequest
    } else if (
      (httpStatus === 503 || httpStatus === 409) &&
      status.errorCode === undefined &&
      SHEET_SIZE_REQUEST_ERROR_CODES.has(body.error)
    ) {
      status.errorCode = body.error
    }
  }
  // Flag logoMethodMaxSizeEnabled: the admin "Max logo size" save from a
  // stale page answers 409 { error: "logoMethodMaxSizeConflict", message,
  // state }. The marker sits in the STRING `error` only (`message`, already
  // mapped above, is the human text), so keep it as errorCode (never
  // overwriting an explicit one) and carry `state` (an object only) so the
  // box can show the stored values without a second GET. Any other 409 body
  // is untouched. Flag off = the status exactly as above.
  if (
    enviroment.logoMethodMaxSizeEnabled &&
    httpStatus === 409 &&
    body.error === LOGO_METHOD_MAX_SIZE_CONFLICT_ERROR_CODE
  ) {
    if (status.errorCode === undefined) {
      status.errorCode = LOGO_METHOD_MAX_SIZE_CONFLICT_ERROR_CODE
    }
    const raw = body as { state?: unknown }
    if (raw.state !== null && typeof raw.state === 'object') {
      status.logoMethodMaxSizeState = raw.state
    }
  }
  // Flag logoMethodMaxSizeByOrgEnabled: the admin "Customer-specific max
  // sizes" save from a stale page answers 409 { error:
  // "logoMethodMaxSizeCustomersConflict", message, state } - same handling
  // as the general box's conflict above, with its own marker and its own
  // state field (the two boxes never read each other's). Any other 409 body
  // is untouched. Flag off = the status exactly as above.
  if (
    enviroment.logoMethodMaxSizeByOrgEnabled &&
    httpStatus === 409 &&
    body.error === LOGO_METHOD_MAX_SIZE_CUSTOMERS_CONFLICT_ERROR_CODE
  ) {
    if (status.errorCode === undefined) {
      status.errorCode = LOGO_METHOD_MAX_SIZE_CUSTOMERS_CONFLICT_ERROR_CODE
    }
    const raw = body as { state?: unknown }
    if (raw.state !== null && typeof raw.state === 'object') {
      status.logoMethodMaxSizeCustomersState = raw.state
    }
  }
  // Flag stockStatusReportAdminEnabled: the admin "Stock status report"
  // save from a stale page answers 409 { message, current } - no marker,
  // `message` (already mapped above) is the human text and `current` is the
  // GET shape as it is stored now. Carry `current` (only an object with the
  // GET's revision + items, so no other 409 body can be mistaken for it)
  // for the page to show without a second GET. Flag off = the status
  // exactly as above.
  if (enviroment.stockStatusReportAdminEnabled && httpStatus === 409) {
    const raw = body as { current?: unknown }
    if (raw.current !== null && typeof raw.current === 'object') {
      const current = raw.current as { revision?: unknown; items?: unknown }
      if (typeof current.revision === 'number' && Array.isArray(current.items)) {
        status.stockStatusReportCurrent = raw.current
      }
    }
  }
  // Flag uploadPendingEnabled: the upload's sync path answers a
  // deterministic supplier rejection (API flag UploadRejectMatchV2Enabled)
  // as 422 { error: "uploadRejected", uploadFailedReason,
  // uploadFailedMessage, message }. The marker sits in the STRING `error`
  // only and `message` (already mapped above) is the friendly English text,
  // so keep the marker as errorCode (never overwriting an explicit one) and
  // carry the code + the supplier's text (strings only) for the translated
  // toast. Any other 422 body is untouched. Flag off = the status exactly
  // as above.
  if (
    enviroment.uploadPendingEnabled &&
    httpStatus === 422 &&
    body.error === UPLOAD_REJECTED_ERROR_CODE
  ) {
    if (status.errorCode === undefined) status.errorCode = UPLOAD_REJECTED_ERROR_CODE
    const raw = body as { uploadFailedReason?: unknown; uploadFailedMessage?: unknown }
    if (typeof raw.uploadFailedReason === 'string') {
      status.uploadFailedReason = raw.uploadFailedReason
    }
    if (typeof raw.uploadFailedMessage === 'string') {
      status.uploadFailedMessage = raw.uploadFailedMessage
    }
  }
  return status
}
